import MultiChoice from "./MultiChoice"
import TextChoice from "./TextChoice"
import SingleChoice from "./SingleChoice"
import EmailAddressField from "./EmailAddressField"
import DepartmentSelector from "./DepartmentSelector"

import { formatNumber } from "lib/NumberHelper"
import {
  Color,
  DefaultStepConfig,
  Equipment,
  StepConfigProps,
  StepConfigBuilder,
  StepName,
  BrandAndModelValue,
  YearValue,
  BodyValue,
  BuyerCarColorValue,
  CarConditionValue,
  EmailAddressValue,
  EngineSizeAndHpValue,
  EquipmentValue,
  EquipmentVariantValue,
  FuelValue,
  GearAndFourWheelDriveValue,
  KmValue,
} from "./carAssemblageTypes"

export const ROOT_OBJECT_NAME = "_root_"

const defaultStepConfig: DefaultStepConfig = {
  apiCall: true,
  apiIds: null,
  apiName: null,
  completeAutomatically: true,
  completed: false,
  component: SingleChoice,
  componentProps: {},
  options: null,
  selectedObjectName: "estimation_car",
  selected: null,
  selectedFromCar: null,
  selectedFromCarDelayed: null,
  valueToLabelMapper: null,
  manualInputs: [],
  allChoices: [],
}

const defaultStepNames: StepName[] = [
  "brand_and_model_name",
  "year",
  "fuel",
  "engine_size_and_hp",
  "equipment_variant",
  "body",
  "has_auto_gear_and_has_4wd",
  "km",
  "car_condition",
  "color",
  "equipment",
  "department",
  "email_address",
]

const stepConfigs: StepConfigBuilder = {
  brand_and_model_name: ({ brandAndModelOptions }) => ({
    options: brandAndModelOptions,
    valueToLabelMapper: (value: BrandAndModelValue) =>
      `${value.brand} ${value.car_model}`,
    selectedFromCar: (
      car: BrandAndModelValue,
      options: StepConfigProps["brandAndModelOptions"]
    ) => {
      if (!car.brand) return
      const brandAndModels = options?.all.find(
        ([brand, _models]) => brand === car.brand
      )
      if (!brandAndModels) return
      const [brand, models] = brandAndModels
      return brand && models.includes(car.car_model)
        ? { brand, car_model: car.car_model }
        : {}
    },
  }),
  year: () => ({
    valueToLabelMapper: ({ year }: YearValue) => year.toString(),
    selectedFromCar: ({ year }: YearValue) => year && { year },
  }),
  fuel: ({ labels }) => ({
    valueToLabelMapper: ({ fuel }: FuelValue) => labels.fuelValues[fuel],
    selectedFromCar: ({ fuel }: FuelValue) => fuel && { fuel },
  }),
  engine_size_and_hp: ({ labels }) => ({
    valueToLabelMapper: ({ engine_size, hp }: EngineSizeAndHpValue) =>
      `${engine_size ? engine_size + " " : ""}${hp} ${labels.hpUnit}`,
    selectedFromCar: ({ engine_size, hp }: EngineSizeAndHpValue) =>
      (engine_size || hp) && { engine_size, hp },
  }),
  equipment_variant: ({ labels }) => ({
    apiCall: false,
    valueToLabelMapper: ({ equipment_variant }: EquipmentVariantValue) =>
      equipment_variant ? equipment_variant : labels.blankEquipmentVariant,
    selectedFromCar: ({ equipment_variant }: EquipmentVariantValue) =>
      equipment_variant && { equipment_variant },
  }),
  body: ({ labels }) => ({
    apiName: "body_and_doors",
    valueToLabelMapper: ({ body, doors }: BodyValue) =>
      `${body ? labels.bodyValues[body] : ""}${doors ? ` ${doors} ${labels.doors}` : ""
      }`,
    selectedFromCar: ({ body, doors }: BodyValue) => {
      if (body && !doors) return { body }
      if (body && doors) return { body, doors }
      if (!body && doors) return { doors }
      return {}
    },
  }),
  has_auto_gear_and_has_4wd: ({ labels }) => ({
    valueToLabelMapper: ({
      has_auto_gear,
      has_4wd,
    }: GearAndFourWheelDriveValue) =>
      `${(has_4wd ? labels.fourWheelDrive : "") + " "}${has_auto_gear ? labels.gears.automatic : labels.gears.manual
      }`,
    selectedFromCar: ({ has_auto_gear, has_4wd }: GearAndFourWheelDriveValue) =>
      (has_auto_gear != null || has_4wd != null) && { has_auto_gear, has_4wd },
  }),
  km: ({ labels, locale }) => ({
    apiCall: false,
    completeAutomatically: false,
    options: { km: "" },
    component: TextChoice,
    componentProps: { type: "number", label: labels.placeholders.km },
    valueToLabelMapper: ({ km }: KmValue) =>
      `${formatNumber(km, locale)} ${labels.mileageUnit}`,
    selectedFromCar: ({ km }: KmValue) => (km ? { km } : null),
  }),
  color: ({ labels }) => ({
    apiCall: false,
    completeAutomatically: false,
    options: labels.colors,
    component: SingleChoice,
    selectedObjectName: "trade_in_valuation",
    valueToLabelMapper: (value: BuyerCarColorValue) =>
      (
        labels.colors.find(
          (color: Color) =>
            color.value.buyer_car_color === value.buyer_car_color
        ) || {}
      ).label || "",
  }),
  equipment: ({ labels }) => ({
    apiIds: ["common_equipment", "additional_equipment"],
    apiCall: false,
    completeAutomatically: false,
    component: MultiChoice,
    valueToLabelMapper: (value: EquipmentValue) =>
      Object.keys(value)
        .reduce<string[]>((acc, key) => {
          if (value[key as Equipment]) {
            acc.push(labels.equipmentLabels[key as Equipment].label)
          }
          return acc
        }, [])
        .join(", "),
    initiallyPreselected: (
      options: Array<{
        options: Array<{ value: EquipmentValue }>
      }>
    ) =>
      options[0]?.options
        .reduce((acc: { value: EquipmentValue }[], e) => acc.concat(e), [])
        .reduce((acc: EquipmentValue, { value }) => {
          for (const key in value) {
            acc[key as Equipment] = value[key as Equipment]
          }
          return acc
        }, {}),
    selectedFromCarDelayed: (
      car: EquipmentValue,
      options: Array<{
        options: Array<{ value: EquipmentValue }>
      }>
    ) =>
      options
        .map(({ options }) => options)
        .reduce((acc: { value: EquipmentValue }[], e) => acc.concat(e), [])
        .reduce((acc: EquipmentValue, { value }) => {
          for (const key in value) {
            if (
              Object.prototype.hasOwnProperty.call(value, key) &&
              car[key as Equipment]
            )
              acc[key as Equipment] = car[key as Equipment]
          }
          return acc
        }, {}),
  }),
  department: ({
    departmentOptions,
    postcodesForTypeaheadUrl,
    departmentParamName,
  }) => ({
    apiCall: false,
    completeAutomatically: false,
    options: departmentOptions,
    component: DepartmentSelector,
    componentProps: { postcodesForTypeaheadUrl },
    selectedObjectName: departmentParamName,
    valueToLabelMapper: (value: string) =>
      (
        departmentOptions?.find((department) => department.value === value) ||
        {}
      ).label || "",
  }),
  car_condition: ({ labels, conditionOptions, conditionAttributes }) => {
    const options = conditionOptions?.length ? conditionOptions : []
    const nestedMultiChoices = conditionAttributes?.length
      ? { choices: conditionAttributes, objectName: "estimation_car" }
      : null

    return {
      apiCall: false,
      completeAutomatically: false,
      options,
      component: SingleChoice,
      componentProps: { fullWidthOptions: true, nestedMultiChoices },
      selectedObjectName: ROOT_OBJECT_NAME,
      valueToLabelMapper: (value: CarConditionValue) =>
        `${labels.condition}: ${options[value.trade_in_valuation.buyer_car_condition_grade - 1]!.label
        }`,
    }
  },
  email_address: () => ({
    apiCall: false,
    completeAutomatically: false,
    selectedObjectName: "trade_in_valuation",
    options: { buyer_email: "" },
    component: EmailAddressField,
    componentProps: { type: "text" },
    valueToLabelMapper: (value: EmailAddressValue) => `${value.buyer_email}`,
  }),
}

export default function getStepConfigs(props: StepConfigProps) {
  let { stepNames = defaultStepNames } = props
  const {
    conditionOptions,
    departmentOptions,
    enableReengagementFlow,
    fromTestDriveForm,
    removeColorsStep,
    user,
  } = props

  if (!(conditionOptions && conditionOptions.length)) {
    stepNames = stepNames.filter((stepName) => stepName !== "car_condition")
  }

  if (!(departmentOptions && departmentOptions.length)) {
    stepNames = stepNames.filter((stepName) => stepName !== "department")
  }
  if (!enableReengagementFlow) {
    stepNames = stepNames.filter((stepName) => stepName !== "email_address")
  }
  if (removeColorsStep) {
    stepNames = stepNames.filter((stepName) => stepName !== "color")
  }
  if (fromTestDriveForm) {
    stepNames = stepNames.filter(
      (stepName) => stepName !== "email_address" && stepName !== "department"
    )
  }
  if (user?.buyer_email) {
    stepNames = stepNames.filter((stepName) => stepName !== "email_address")
  }

  return stepNames.map((stepName) => ({
    ...defaultStepConfig,
    stepName,
    ...stepConfigs[stepName]({
      brandAndModelOptions: props.brandAndModelOptions,
      labels: props.labels,
      conditionOptions: props.conditionOptions,
      conditionAttributes: props.conditionAttributes,
      departmentOptions: props.departmentOptions,
      locale: props.locale,
      postcodesForTypeaheadUrl: props.postcodesForTypeaheadUrl,
      departmentParamName: props.departmentParamName,
    }),
    valid: true,
  }))
}
